import React, { FC } from 'react';
import styled from 'styled-components';
import { Row, TableHeading } from 'src/components/widgets/table';
import { useFeatureFlag } from 'src/shared/feature-flags';
import { ResponsiveProductTable } from './productTable';
import Stack from 'src/components/widgets/stack/stack';
import {
    SkeletonBlock,
    SkeletonFiltersRow,
    SkeletonPageHeader,
    SkeletonPageShell,
    SkeletonRow,
    SkeletonStack,
    SkeletonTabs
} from 'src/components/widgets/skeleton';

const ProductsSkeletonShell = styled(SkeletonPageShell)``;

const ProductsSkeletonHeader = styled(SkeletonPageHeader)``;

const ProductTaskStatsGridSkeleton = styled.div`
    display: grid;
    grid-template-columns: repeat(5, minmax(120px, 1fr));
    gap: 12px;
    min-width: 0;

    @media (max-width: 1100px) {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    @media (max-width: 700px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
`;

const ProductTableCellSkeleton = styled(SkeletonBlock)`
    max-width: 100%;
`;

const TaskDetailsGridSkeleton = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 24px;
    min-width: 0;

    @media (max-width: 968px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

const TaskSectionSkeleton = styled.div`
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 8px;
    background: #ffffff;
    padding: 24px;
    min-width: 0;
`;

const ProductInfoSkeletonRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 12px 0;
    border-bottom: 1px solid ${(props) => props.theme.app.borderColor};
    min-width: 0;

    &:last-child {
        border-bottom: 0;
    }
`;

export const ProductInfoRowsSkeleton: FC = () => (
    <SkeletonStack aria-hidden='true' $gap='0'>
        {Array.from({ length: 4 }).map((_, index) => (
            <ProductInfoSkeletonRow key={index}>
                <SkeletonBlock $width='90px' $height='13px' />
                <SkeletonRow $gap='10px' $justify='flex-end'>
                    {index === 0 && <SkeletonBlock $width='40px' $height='40px' $radius='4px' />}
                    <SkeletonBlock $width={index === 0 ? '160px' : '92px'} $height='13px' />
                </SkeletonRow>
            </ProductInfoSkeletonRow>
        ))}
    </SkeletonStack>
);

export const ProductTableRowsSkeleton: FC<{ rows?: number; productCategoriesEnabled: boolean }> = ({ rows = 8, productCategoriesEnabled }) => (
    <>
        {Array.from({ length: rows }).map((_, index) => (
            <Row
                key={index}
                cells={[
                    <ProductTableCellSkeleton $width='18px' $height='18px' $radius='4px' />,
                    <ProductTableCellSkeleton $width='48px' $height='48px' $radius='8px' />,
                    <SkeletonStack $gap='8px'>
                        <ProductTableCellSkeleton $width={index % 2 === 0 ? '76%' : '58%'} $height='16px' />
                        <ProductTableCellSkeleton $width='42%' $height='12px' />
                    </SkeletonStack>,
                    <ProductTableCellSkeleton $width={index % 2 === 0 ? '72%' : '54%'} $height='14px' />,
                    ...(productCategoriesEnabled ? [<ProductTableCellSkeleton $width='82px' $height='20px' $radius='10px' />] : []),
                    <ProductTableCellSkeleton $width='24px' $height='24px' $round />,
                    <ProductTableCellSkeleton $width='92px' $height='24px' $radius='12px' />,
                    <ProductTableCellSkeleton $width='78px' $height='24px' $radius='12px' />,
                    <ProductTableCellSkeleton $width='42px' $height='14px' />,
                    <SkeletonRow $gap='8px' $justify='flex-end'>
                        <ProductTableCellSkeleton $width='28px' $height='28px' $radius='4px' />
                        <ProductTableCellSkeleton $width='28px' $height='28px' $radius='4px' />
                    </SkeletonRow>
                ]}
            />
        ))}
    </>
);

export const ProductTableSkeleton: FC<{ headings: TableHeading[]; rows?: number; productCategoriesEnabled: boolean }> = ({
    headings,
    rows,
    productCategoriesEnabled
}) => (
    <ResponsiveProductTable headings={headings} data-testid='products-table' compact $productCategoriesEnabled={productCategoriesEnabled}>
        <ProductTableRowsSkeleton rows={rows} productCategoriesEnabled={productCategoriesEnabled} />
    </ResponsiveProductTable>
);

const ProductsSkeletonWorkspace = styled.div<{ $showCategories: boolean }>`
    display: grid;
    grid-template-columns: ${(props) => (props.$showCategories ? '260px minmax(0, 1fr)' : 'minmax(0, 1fr)')};
    gap: 16px;
    align-items: start;
`;

const ProductsSkeletonMain = styled(Stack)`
    min-width: 0;
`;

export const ProductsPageSkeleton: FC = () => {
    const productCategoriesEnabled = useFeatureFlag('productCategories');
    const headings = ['45px', '85px', '1.2fr', '1fr', ...(productCategoriesEnabled ? ['0.8fr'] : []), '0.4fr', '0.6fr', '0.6fr', '0.6fr', '1fr'].map(
        (width, index, columns) =>
            [
                <SkeletonBlock $width={index === 0 ? '18px' : index === 1 || index === columns.length - 1 ? '1px' : '42px'} $height='12px' />,
                width
            ] as TableHeading
    );

    return (
        <ProductsSkeletonShell aria-hidden='true'>
            <ProductsSkeletonHeader>
                <SkeletonStack $gap='8px' $flex='1'>
                    <SkeletonBlock $width='min(260px, 100%)' $height='26px' />
                    <SkeletonBlock $width='min(520px, 100%)' $height='13px' />
                </SkeletonStack>
                <SkeletonRow $gap='8px'>
                    <SkeletonBlock $width='132px' $height='36px' $radius='6px' />
                    <SkeletonBlock $width='140px' $height='36px' $radius='6px' />
                    <SkeletonBlock $width='88px' $height='36px' $radius='6px' />
                </SkeletonRow>
            </ProductsSkeletonHeader>
            <SkeletonTabs tabWidths={['72px', '124px']} />
            <Stack $gap='16px'>
                <SkeletonBlock $width='100%' $height='70px' $radius='6px' />
                <ProductsSkeletonWorkspace $showCategories={productCategoriesEnabled}>
                    {productCategoriesEnabled && (
                        <SkeletonStack $gap='16px'>
                            <SkeletonBlock $width='120px' $height='20px' />
                            <SkeletonBlock $height='32px' $radius='6px' />
                            <SkeletonBlock $width='75%' $height='16px' />
                            <SkeletonBlock $width='60%' $height='16px' />
                        </SkeletonStack>
                    )}
                    <ProductsSkeletonMain $gap='8px'>
                        <SkeletonFiltersRow controlWidths={['214px', '214px', '214px', '214px', '300px']} $margin='0' />
                        <ProductTableSkeleton headings={headings} productCategoriesEnabled={productCategoriesEnabled} />
                    </ProductsSkeletonMain>
                </ProductsSkeletonWorkspace>
            </Stack>
        </ProductsSkeletonShell>
    );
};

export const ProductDuplicationTaskDetailPageSkeleton: FC = () => (
    <ProductsSkeletonShell aria-hidden='true'>
        <ProductsSkeletonHeader>
            <SkeletonStack $gap='8px' $flex='1'>
                <SkeletonBlock $width='min(320px, 100%)' $height='26px' />
                <SkeletonBlock $width='min(420px, 100%)' $height='13px' />
            </SkeletonStack>
            <SkeletonBlock $width='92px' $height='36px' $radius='6px' />
        </ProductsSkeletonHeader>
        <SkeletonStack $gap='18px'>
            <TaskSectionSkeleton>
                <SkeletonStack $gap='14px'>
                    <SkeletonBlock $width='110px' $height='20px' />
                    <ProductTaskStatsGridSkeleton>
                        <SkeletonBlock $height='64px' $radius='8px' />
                        <SkeletonBlock $height='64px' $radius='8px' />
                        <SkeletonBlock $height='64px' $radius='8px' />
                        <SkeletonBlock $height='64px' $radius='8px' />
                        <SkeletonBlock $height='64px' $radius='8px' />
                    </ProductTaskStatsGridSkeleton>
                </SkeletonStack>
            </TaskSectionSkeleton>
            <TaskSectionSkeleton>
                <SkeletonBlock $width='100px' $height='20px' $margin='0 0 18px' />
                <TaskDetailsGridSkeleton>
                    <ProductInfoRowsSkeleton />
                    <ProductInfoRowsSkeleton />
                </TaskDetailsGridSkeleton>
            </TaskSectionSkeleton>
            <TaskSectionSkeleton>
                <SkeletonStack $gap='12px'>
                    <SkeletonBlock $width='92px' $height='20px' />
                    <SkeletonBlock $height='12px' $radius='6px' />
                    <SkeletonBlock $width='min(520px, 100%)' $height='44px' $radius='6px' />
                </SkeletonStack>
            </TaskSectionSkeleton>
        </SkeletonStack>
    </ProductsSkeletonShell>
);
